import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Modal from "@/common/elements/Modal";
import _ from "lodash";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FormContainer } from "../InmateDetails/styles";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { StyledGridRow, StyledGridCol } from "@/common/styles";
import {
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";

export const MODE = { NEW: "NEW", EDIT: "EDIT" };

const CourtInformationModal = forwardRef(
  (
    {
      show,
      onHide,
      formData,
      onCourtInformationSaveClick,
      courtInfoRef,
      mode = MODE.NEW,
      startingPoint = "",
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const [courtInfo, setCourtInfo] = useState(null);
    const fullSearch = useSelector((state) => state.fullSearch);
    const { bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );

    useEffect(() => {
      setCourtInfo(() => ({
        ...formData,
        courtDateTime: isNonEmptyVariable(formData?.courtDateTime)
          ? dayjs(formData?.courtDateTime)
          : null,
        expectedBackDateTime: isNonEmptyVariable(
          formData?.expectedBackDateTime
        )
          ? dayjs(formData?.expectedBackDateTime)
          : null,
      }));
    }, [formData]);

    useEffect(() => {
      form.resetFields();
      form.setFieldsValue(courtInfo);
    }, [courtInfo]);

    const onModalHide = () => {
      onHide();
    };

    useImperativeHandle(courtInfoRef, () => ({
      form: form,
    }));

    const courtDateTime = Form.useWatch("courtDateTime", form);

    useEffect(() => {
      if (isNonEmptyVariable(courtDateTime)) {
        form.setFieldValue(
          "expectedBackDateTime",
          courtDateTime.hour(16).minute(0)
        );
      }
    }, [courtDateTime]);

    return (
      <Modal
        show={show}
        onHide={onModalHide}
        header={
          mode === MODE.EDIT
            ? "Modify Court Information"
            : "Add Court Information"
        }
        size="xl"
        footerActions={[
          {
            key: "save",
            text: mode === MODE.EDIT ? "Save Changes" : "Add Information",
            onClick: onCourtInformationSaveClick,
          },
        ]}
        footerCloseAction
        footerCloseActionText="Close"
      >
        <FieldSecurityProvider
          entity={SCHEMA_NAME.COURT}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          <Form form={form} initialValues={courtInfo}>
            <FormContainer>
              <StyledGridRow gutter={16}>
                {/* Court Name */}
                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName="courtNameID"
                    formItemProps={{
                      name: "courtNameID",
                      label: "Court Name",
                      required: !fullSearch,
                      rules: !fullSearch
                        ? [
                            {
                              required: true,
                              message: "Court Name is required",
                            },
                          ]
                        : [],
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList(
                        "Court",
                        false,
                        false,
                        true
                      )}
                      placeholder="Court Name"
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Court Date Time */}
                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="courtDateTime"
                    formItemProps={{
                      name: "courtDateTime",
                      label: "Court Date & Time",
                      required: !fullSearch,
                      rules: !fullSearch
                        ? [
                            {
                              required: true,
                              message: "Court Date & Time is required",
                            },
                          ]
                        : [],
                    }}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Expected Back */}
                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="expectedBackDateTime"
                    formItemProps={{
                      name: "expectedBackDateTime",
                      label: "Expected Back Date & Time",
                    }}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      fromDateDisable={courtDateTime?.format("MM/DD/YYYY")}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Judge */}
                <StyledGridCol lg={8}>
                  <SecureFormItem
                    fieldName="judgeAddressBookID"
                    formItemProps={{
                      name: "judgeAddressBookID",
                      label: "Judge",
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList("Judge List")}
                      placeholder="Judge"
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Disposition */}
                <StyledGridCol lg={6}>
                  <SecureFormItem
                    fieldName="offenderDispositionID"
                    formItemProps={{
                      name: "offenderDispositionID",
                      label: "Disposition",
                    }}
                  >
                    <Select
                      options={getOptionsFromRefList(
                        "OffenderDisposition",
                        false,
                        false,
                        true
                      )}
                      placeholder="Disposition"
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Comments */}
                <StyledGridCol lg={24}>
                  <SecureFormItem
                    fieldName="comments"
                    formItemProps={{
                      name: "comments",
                      label: "Comments",
                    }}
                  >
                    <TextField inputType="textarea" rows={5} />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>
            </FormContainer>
          </Form>
        </FieldSecurityProvider>
      </Modal>
    );
  }
);

export default CourtInformationModal;